import React, { useState, useEffect, useMemo } from 'react';
import {
  CardBody,
  Row,
  Col,
  Card,
  Container,
  CardHeader,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalBody,
  Label,
  Input,
  Button,
} from 'reactstrap';
import DataTable from 'react-data-table-component';
import BreadCrumb from '../../../Components/Common/BreadCrumb';
import DeleteModal from '../../../Components/Common/DeleteModal';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { proHydroApi } from '../../../helpers/crude_api_helper';

// Backend entity: CargoCategory ("Crude Category").
// Endpoints per api-contract-cargo-category.md (base {host}/v1):
//   list   GET    /v1/CargoCategories
//   create POST   /v1/CargoCategories
//   update PUT    /v1/CargoCategories/{id}
//   delete DELETE /v1/CargoCategories/{id}
// Field mapping: API `cargocategory` <-> UI "Cargo Category"; `cargoName` <-> "Cargo Name"; `cargoCode` <-> "Cargo Code".
// No pagination, no sort-order endpoint, no isActive/status field on this entity
// (unlike TemplateInformationCategory) — list response is a flat `items` array.
const ENDPOINT = '/v1/CargoCategories';

const emptyForm = {
  id: null,
  cargocategory: '',
  cargoName: '',
  cargoCode: '',
};

const CrudeCategoryView = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const [modalForm, setModalForm] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [errors, setErrors] = useState({});

  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  const [searchKeyword, setSearchKeyword] = useState('');

  document.title = 'Crude Category';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const result = await proHydroApi.get(ENDPOINT);
      setItems(Array.isArray(result?.items) ? result.items : []);
    } catch (error) {
      toast.error(`Failed to load data: ${error}`);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors({ ...errors, [name]: '' });
  };

  // Client-side pre-check for a nicer UX; the backend's own duplicate check
  // only covers Cargocategory + CargoName (not CargoCode) among non-deleted
  // rows — mirror that scope here so this doesn't block legitimate saves.
  const isDuplicate = () => {
    const category = (formData.cargocategory || '').trim().toLowerCase();
    const name = (formData.cargoName || '').trim().toLowerCase();
    return items.some(
      row =>
        (row.cargocategory || '').trim().toLowerCase() === category &&
        (row.cargoName || '').trim().toLowerCase() === name &&
        row.id !== formData.id
    );
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.cargocategory || !formData.cargocategory.trim()) {
      newErrors.cargocategory = 'Cargo Category is required';
    }
    if (!formData.cargoName || !formData.cargoName.trim()) {
      newErrors.cargoName = 'Cargo Name is required';
    }
    if (!formData.cargoCode || !formData.cargoCode.trim()) {
      newErrors.cargoCode = 'Cargo Code is required';
    }
    if (
      formData.cargocategory &&
      formData.cargoName &&
      formData.cargoCode &&
      isDuplicate()
    ) {
      newErrors.cargoName = 'Cargo Name already exists in this Cargo Category';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNewModal = () => {
    setFormData(emptyForm);
    setErrors({});
    setModalForm(true);
  };

  const handleUpdateModal = row => {
    setFormData({
      id: row.id,
      cargocategory: row.cargocategory || '',
      cargoName: row.cargoName || '',
      cargoCode: row.cargoCode || '',
    });
    setErrors({});
    setModalForm(true);
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    if (formData.id) {
      try {
        await proHydroApi.put(`${ENDPOINT}/${formData.id}`, {
          id: formData.id,
          cargocategory: formData.cargocategory.trim(),
          cargoName: formData.cargoName.trim(),
          cargoCode: formData.cargoCode.trim(),
        });
        toast.success('Crude Category has been updated');
      } catch (error) {
        toast.error(`Failed to update data: ${error}`);
      } finally {
        setModalForm(false);
        fetchData();
      }
    } else {
      try {
        await proHydroApi.create(ENDPOINT, {
          cargocategory: formData.cargocategory.trim(),
          cargoName: formData.cargoName.trim(),
          cargoCode: formData.cargoCode.trim(),
        });
        toast.success('Crude Category has been added');
      } catch (error) {
        toast.error(`Failed to save data: ${error}`);
      } finally {
        setModalForm(false);
        fetchData();
      }
    }
  };

  const handleDeleteModal = row => {
    setSelectedRow(row);
    setDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!selectedRow) return;
    try {
      await proHydroApi.delete(`${ENDPOINT}/${selectedRow.id}`);
      toast.success('Crude Category has been deleted');
    } catch (error) {
      toast.error(`Failed to delete data: ${error}`);
    } finally {
      setDeleteModal(false);
      setSelectedRow(null);
      fetchData();
    }
  };

  const filteredItems = useMemo(() => {
    const keyword = searchKeyword.trim().toLowerCase();
    if (!keyword) return items;
    return items.filter(row => {
      const haystack = [
        row.cargocategory || '',
        row.cargoName || '',
        row.cargoCode || '',
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(keyword);
    });
  }, [items, searchKeyword]);

  const headerLabel = label => (
    <span className='form-label text-uppercase fw-semibold fs-13 mb-0'>
      {label}
    </span>
  );

  const columns = [
    {
      name: headerLabel('Cargo Category'),
      selector: row => row.cargocategory,
      sortable: true,
      wrap: true,
    },
    {
      name: headerLabel('Cargo Name'),
      selector: row => row.cargoName,
      sortable: true,
      wrap: true,
    },
    {
      name: headerLabel('Cargo Code'),
      selector: row => row.cargoCode,
      sortable: true,
      wrap: true,
    },
    {
      name: headerLabel('Action'),
      width: '110px',
      center: true,
      cell: row => (
        <div className='d-flex gap-1 justify-content-end'>
          <button
            className='btn btn-soft-info btn-sm btn-icon'
            onClick={() => handleUpdateModal(row)}
          >
            <i className='ri-pencil-fill align-bottom'></i>
          </button>
          <button
            className='btn btn-soft-danger btn-sm btn-icon'
            onClick={() => handleDeleteModal(row)}
          >
            <i className='ri-delete-bin-6-line align-bottom'></i>
          </button>
        </div>
      ),
    },
  ];

  return (
    <React.Fragment>
      <div className='page-content'>
        <DeleteModal
          show={deleteModal}
          onDeleteClick={handleDelete}
          onCloseClick={() => setDeleteModal(false)}
        />
        <Container fluid>
          <BreadCrumb title='Crude Category' pageTitle='CRUDE' />

          <Row>
            <Col lg={12}>
              <Card id='CrudeCategoryList'>
                <CardHeader className='border-0'>
                  <div className='d-flex align-items-center flex-wrap gap-2'>
                    <h5 className='card-title mb-0 flex-grow-1'>
                      Crude Category
                    </h5>
                    <div className='flex-shrink-0'>
                      <div className='d-flex gap-2 flex-wrap'>
                        <div className='search-box'>
                          <Input
                            type='text'
                            className='form-control form-control-sm'
                            placeholder='Search...'
                            value={searchKeyword}
                            onChange={e => setSearchKeyword(e.target.value)}
                          />
                        </div>
                        <button
                          type='button'
                          className='btn btn-primary btn-label right ms-auto nexttab btn-sm'
                          onClick={handleNewModal}
                        >
                          <i className='ri-play-list-add-line label-icon align-middle fs-16 ms-2'></i>{' '}
                          <span className='d-none d-sm-inline'>
                            Add Crude Category
                          </span>
                          <span className='d-sm-none'>New</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </CardHeader>

                <CardBody className='pt-0'>
                  <div
                    className='table-responsive'
                    style={{
                      minHeight: 'auto',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <DataTable
                      columns={columns}
                      highlightOnHover
                      data={filteredItems}
                      pagination
                      paginationPerPage={10}
                      paginationRowsPerPageOptions={[10, 25, 50, 100]}
                      wrap
                      dense
                      striped
                      progressPending={loading}
                      fixedHeader={false}
                      noDataComponent={
                        <div
                          style={{
                            padding: '20px',
                            textAlign: 'center',
                            color: '#6c757d',
                          }}
                        >
                          <i
                            className='ri-database-2-line'
                            style={{
                              fontSize: '32px',
                              marginBottom: '8px',
                              display: 'block',
                            }}
                          ></i>
                          No crude category found
                        </div>
                      }
                      customStyles={{
                        header: {
                          style: {
                            backgroundColor: '#f0f0f0',
                            color: '#333',
                            fontWeight: 'bold',
                          },
                        },
                        headCells: {
                          style: {
                            backgroundColor: '#f0f0f0',
                            fontWeight: 'bold',
                            fontSize: '14px',
                            paddingLeft: '12px',
                            paddingRight: '12px',
                            borderRight: '1px solid #dee2e6',
                            position: 'relative',
                            overflow: 'visible',
                          },
                        },
                        cells: {
                          style: {
                            paddingLeft: '12px',
                            paddingRight: '12px',
                            wordBreak: 'break-word',
                            borderRight: '1px solid #dee2e6',
                          },
                        },
                        rows: {
                          style: {
                            minHeight: '50px',
                            '&:hover': {
                              backgroundColor: '#f8f9fa',
                            },
                          },
                        },
                        table: {
                          style: {
                            width: '100%',
                            minWidth: '100%',
                          },
                        },
                      }}
                    />

                    <ToastContainer closeButton={false} limit={1} />
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Modal
            isOpen={modalForm}
            role='dialog'
            autoFocus={true}
            centered
            id='addCrudeCategoryModal'
            toggle={() => setModalForm(!modalForm)}
            data-bs-backdrop='static'
            size='md'
          >
            <ModalHeader
              className='bg-soft-info'
              toggle={() => setModalForm(!modalForm)}
            >
              <span className='modal-title text-info fw-bold'>
                {formData.id !== null
                  ? 'Edit Crude Category'
                  : 'Add Crude Category'}
              </span>
            </ModalHeader>

            <ModalBody>
              <div className='d-flex mt-2'>
                <Col sm={12}>
                  <Row>
                    <Col sm={12}>
                      <div className='mb-3'>
                        <Label for='cargocategory' className='form-label'>
                          Cargo Category <span className='text-danger'>*</span>
                        </Label>
                        <Input
                          type='text'
                          id='cargocategory'
                          name='cargocategory'
                          className='form-control'
                          maxLength={10}
                          invalid={!!errors.cargocategory}
                          value={formData.cargocategory || ''}
                          onChange={handleChange}
                        />
                        {errors.cargocategory && (
                          <div className='invalid-feedback d-block'>
                            {errors.cargocategory}
                          </div>
                        )}
                      </div>
                    </Col>
                    <Col sm={12}>
                      <div className='mb-3'>
                        <Label for='cargoName' className='form-label'>
                          Cargo Name <span className='text-danger'>*</span>
                        </Label>
                        <Input
                          type='text'
                          id='cargoName'
                          name='cargoName'
                          className='form-control'
                          maxLength={100}
                          invalid={!!errors.cargoName}
                          value={formData.cargoName || ''}
                          onChange={handleChange}
                        />
                        {errors.cargoName && (
                          <div className='invalid-feedback d-block'>
                            {errors.cargoName}
                          </div>
                        )}
                      </div>
                    </Col>
                    <Col sm={12}>
                      <div className='mb-3'>
                        <Label for='cargoCode' className='form-label'>
                          Cargo Code <span className='text-danger'>*</span>
                        </Label>
                        <Input
                          type='text'
                          id='cargoCode'
                          name='cargoCode'
                          className='form-control'
                          maxLength={100}
                          invalid={!!errors.cargoCode}
                          value={formData.cargoCode || ''}
                          onChange={handleChange}
                        />
                        {errors.cargoCode && (
                          <div className='invalid-feedback d-block'>
                            {errors.cargoCode}
                          </div>
                        )}
                      </div>
                    </Col>
                  </Row>
                </Col>
              </div>
            </ModalBody>

            <ModalFooter>
              <Button
                color='light'
                className='btn-sm'
                onClick={() => setModalForm(false)}
              >
                Cancel
              </Button>
              <Button color='primary' className='btn-sm' onClick={handleSave}>
                Save
              </Button>
            </ModalFooter>
          </Modal>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default CrudeCategoryView;
